import { useState } from "react";
import { CalendarRange, ChevronUp, ExternalLink } from "lucide-react";
import { OUTCOMES } from "./TradeTable";
import { JournalChart } from "./JournalChart";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const getRange = (sel) => {
  const now = new Date();
  if (sel.type === "week") {
    const start = new Date(now);
    start.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 7);
    return [start, end];
  }
  if (sel.type === "month") return [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 1)];
  if (sel.type === "year") return [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear() + 1, 0, 1)];
  if (sel.type === "customMonth") return [new Date(sel.year, sel.month, 1), new Date(sel.year, sel.month + 1, 1)];
  if (sel.type === "customYear") return [new Date(sel.year, 0, 1), new Date(sel.year + 1, 0, 1)];
  return null;
};

const rangeLabel = (sel) => {
  if (sel.type === "week") return "Settimana attuale";
  if (sel.type === "month") return `${MONTHS[new Date().getMonth()]} ${new Date().getFullYear()}`;
  if (sel.type === "year") return `Anno ${new Date().getFullYear()}`;
  if (sel.type === "customMonth") return `${MONTHS[sel.month]} ${sel.year}`;
  if (sel.type === "customYear") return `Anno ${sel.year}`;
  return "";
};

const fmtDate = (dt) => {
  try { return new Date(dt).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return dt; }
};

export const PeriodReview = ({ trades }) => {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({ type: null, month: new Date().getMonth(), year: new Date().getFullYear() });

  if (!trades || trades.length === 0) return null;

  const currentYear = new Date().getFullYear();
  const tradeYears = trades.map((t) => new Date(t.trade_datetime).getFullYear()).filter((y) => !isNaN(y));
  const minYear = Math.min(currentYear, ...(tradeYears.length ? tradeYears : [currentYear]));
  const years = [];
  for (let y = currentYear; y >= minYear; y--) years.push(y);

  const range = getRange(sel);
  const filtered = range
    ? trades.filter((t) => {
        const d = new Date(t.trade_datetime);
        return !isNaN(d) && d >= range[0] && d < range[1];
      })
    : [];

  const wins = filtered.filter((t) => t.outcome === "win").length;
  const losses = filtered.filter((t) => t.outcome === "loss").length;
  const winRate = wins + losses > 0 ? ((wins / (wins + losses)) * 100).toFixed(1) : null;
  const cum = filtered.reduce((s, t) => s + (parseFloat(t.percentage) || 0), 0);
  const cumRounded = Math.round(cum * 100) / 100;

  const quickPill = (active) => ({
    border: `1px solid ${active ? "var(--mm-gold)" : "var(--mm-border)"}`,
    color: active ? "var(--mm-gold)" : "var(--mm-text-secondary)",
    backgroundColor: active ? "rgba(47,216,168,0.1)" : "transparent",
    fontFamily: "'Space Grotesk', sans-serif",
  });

  const sorted = [...filtered].sort((a, b) => (b.trade_datetime || "").localeCompare(a.trade_datetime || ""));

  return (
    <div className="mb-8" data-testid="period-review-section">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-3 w-full px-5 py-4 transition-colors hover:bg-white/5 glass-card"
        style={{ border: `1px solid ${open ? "var(--mm-gold)" : "var(--mm-border)"}` }}
        data-testid="period-toggle-btn">
        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(47,216,168,0.12)" }}>
          <CalendarRange size={20} style={{ color: "var(--mm-gold)" }} />
        </div>
        <div className="flex-1 text-left">
          <p className="text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>Revisione Periodo — I tuoi rendimenti</p>
          <p className="text-xs" style={{ color: "var(--mm-text-muted)" }}>Settimana, mese o anno: rivedi solo i rendimenti del periodo che ti interessa.</p>
        </div>
        <ChevronUp size={18} style={{ color: "var(--mm-text-muted)", transform: open ? "none" : "rotate(180deg)", transition: "transform 0.2s" }} />
      </button>

      {open && (
        <div className="glass-card p-6 sm:p-8 mt-2" data-testid="period-panel">
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <button onClick={() => setSel({ ...sel, type: "week" })} className="px-4 py-2 text-xs font-bold uppercase transition-colors" style={quickPill(sel.type === "week")} data-testid="period-week">Settimana attuale</button>
            <button onClick={() => setSel({ ...sel, type: "month" })} className="px-4 py-2 text-xs font-bold uppercase transition-colors" style={quickPill(sel.type === "month")} data-testid="period-month">Mese attuale</button>
            <button onClick={() => setSel({ ...sel, type: "year" })} className="px-4 py-2 text-xs font-bold uppercase transition-colors" style={quickPill(sel.type === "year")} data-testid="period-year">Anno attuale</button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-2">
            <div>
              <label className="text-xs uppercase font-semibold block mb-2" style={{ color: "var(--mm-text-muted)", letterSpacing: "0.1em" }}>Oppure scegli un mese</label>
              <div className="flex gap-2">
                <select value={sel.type === "customMonth" ? sel.month : ""} onChange={(e) => e.target.value !== "" && setSel({ ...sel, type: "customMonth", month: parseInt(e.target.value) })}
                  className="admin-input" data-testid="period-month-select">
                  <option value="">Mese —</option>
                  {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
                </select>
                <select value={sel.year} onChange={(e) => setSel({ ...sel, year: parseInt(e.target.value), type: sel.type === "customMonth" ? "customMonth" : sel.type })}
                  className="admin-input" style={{ maxWidth: "110px" }} data-testid="period-month-year-select">
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase font-semibold block mb-2" style={{ color: "var(--mm-text-muted)", letterSpacing: "0.1em" }}>Oppure scegli un anno intero</label>
              <select value={sel.type === "customYear" ? sel.year : ""} onChange={(e) => e.target.value !== "" && setSel({ ...sel, type: "customYear", year: parseInt(e.target.value) })}
                className="admin-input" style={{ maxWidth: "160px" }} data-testid="period-year-select">
                <option value="">Anno —</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {sel.type && (
            <div className="pt-6 mt-4" style={{ borderTop: "1px solid var(--mm-border)" }} data-testid="period-results">
              <p className="text-sm font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }} data-testid="period-label">
                Rendimenti — <span className="gold-text">{rangeLabel(sel)}</span>
                <span className="text-xs font-normal ml-2" style={{ color: "var(--mm-text-muted)" }}>({filtered.length} trade)</span>
              </p>

              {filtered.length === 0 ? (
                <p className="text-sm text-center py-8" style={{ color: "var(--mm-text-muted)" }} data-testid="period-empty">Nessun trade in questo periodo.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    {[
                      { label: "Trade", value: filtered.length, color: "var(--mm-gold)" },
                      { label: "Win / Loss", value: `${wins} / ${losses}`, color: "var(--mm-text-primary)" },
                      { label: "Win Rate", value: winRate != null ? `${winRate}%` : "—", color: "#64C8FF" },
                      { label: "% Cumulata", value: `${cumRounded >= 0 ? "+" : ""}${cumRounded}%`, color: cumRounded >= 0 ? "#2FD8A8" : "#EF4444" },
                    ].map((s, i) => (
                      <div key={i} className="glass-card p-4 text-center" data-testid={`period-stat-${i}`}>
                        <p className="text-xl font-bold" style={{ color: s.color, fontFamily: "'Space Grotesk', sans-serif" }}>{s.value}</p>
                        <p className="text-xs uppercase mt-1" style={{ color: "var(--mm-text-muted)", letterSpacing: "0.1em" }}>{s.label}</p>
                      </div>
                    ))}
                  </div>

                  <JournalChart trades={filtered} />

                  <div className="glass-card" data-testid="period-trades-list">
                    {sorted.map((t) => {
                      const meta = OUTCOMES.find((o) => o.value === t.outcome) || OUTCOMES[0];
                      const pct = parseFloat(t.percentage) || 0;
                      return (
                        <div key={t.id} className="flex items-center gap-4 px-4 py-3" style={{ borderBottom: "1px solid var(--mm-border)" }} data-testid={`period-trade-${t.id}`}>
                          <span className="text-sm w-24 flex-shrink-0" style={{ color: "var(--mm-text-secondary)" }}>{fmtDate(t.trade_datetime)}</span>
                          <span className="text-sm font-semibold w-24 flex-shrink-0" style={{ color: "var(--mm-text-primary)" }}>{t.pair}</span>
                          <span className="px-2 py-0.5 text-[10px] font-bold uppercase flex-shrink-0" style={{ color: meta.color, backgroundColor: `${meta.color}1f`, border: `1px solid ${meta.color}55` }}>{meta.label}</span>
                          <span className="flex-1" />
                          {t.screenshot_url && (
                            <a href={t.screenshot_url} target="_blank" rel="noreferrer" style={{ color: "var(--mm-gold)" }}><ExternalLink size={13} /></a>
                          )}
                          <span className="text-sm font-bold w-16 text-right" style={{ color: pct >= 0 ? "#2FD8A8" : "#EF4444", fontFamily: "'Space Grotesk', sans-serif" }}>
                            {pct >= 0 ? "+" : ""}{pct}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
